import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { Flex } from "@chakra-ui/react";
import dayjs from "dayjs";
import { lazy, Suspense, useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { roleFilterSchema } from "./schema/roles.schema";
import { Rol } from "../role-asignation/schema/role-assignation.schema";
import { RoleListFilter } from "@/components/common/section-components/filters/RoleListFilter";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import {
  handleDeleteRoleExternal,
  clearFiltersHandler,
  submitFiltersHandler,
  fetchRolesHandler,
} from "./schema/handler-functions";
import { RoleActions } from "@/components/common/section-components/action/RoleActions";
import { NoData } from "@/components/common/section-components/NoData";
import { usePolling } from "@/hooks/usePolling";
import { isEmpty } from "@/utils/query-string";

export const RolesPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const navigate = useNavigate();
  const [selection, setSelection] = useState<string[]>([]);

  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [loadingDeleteRol, setLoadingDeleteRol] = useState<boolean>(false);
  const { control, handleSubmit, setValue, getValues, watch } = useForm({
    resolver: zodResolver(roleFilterSchema),
  });

  const [currentFilters, setCurrentFilters] = useState<{
    Nombre?: string;
  }>({
    Nombre: "",
  });

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [availableRoles, setAvailableRoles] = useState<Rol[]>([]);
  const { userPermissions } = useRoles();
  const fetchRoles = async (filters?: any, showLoad = true) => {
    await fetchRolesHandler({
      filters,
      showLoad,
      setLoadingRoles,
      setCurrentFilters,
      setAvailableRoles,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) fetchRoles();
  }, [isActive]);

  usePolling({
    callback: () => fetchRoles({ ...currentFilters }, false),
    delay: 10000,
    isActive,
  });

  const hasSelection = selection.length > 0;
  const indeterminate =
    hasSelection && selection.length < availableRoles?.length;

  const headerElements = [
    {
      label: "Rol",
      value: "nombre",
    },
  ];

  const handleAddRole = () => {
    navigate(AppRoutes.CREATE_ROLE);
  };

  const { fetchPermissions } = useRoles();

  const handleDeleteRole = async () => {
    await handleDeleteRoleExternal({
      currentFilters: currentFilters,
      fetchRoles,
      selection,
      setLoadingDeleteRol,
      setOpenConfirm,
      navigate,
      fetchPermissions,
    });
  };

  const clearFilters = async () => {
    clearFiltersHandler({
      setFiltersApplied,
      setValue,
    });

    submitFilters?.(getValues());
  };

  const submitFilters = async (event: FieldValues) => {
    submitFiltersHandler({
      setFiltersApplied,
      setCurrentFilters,
      setOpenFilters,
      fetchRoles,
      event,
      navigate,
      fetchPermissions,
    });
  };

  const handleEdit = (el: any) => {
    navigate(AppRoutes.UPDATE_ROLE.replace(":id", String(el?.id)));
  };

  const watchedValues = watch();
  const isFormEmpty = Object.values(watchedValues).every((val) => isEmpty(val));

  const getActionElements = (item: Rol) => (
    <RoleActions handleEdit={handleEdit} item={item} />
  );

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.ROLES_PERMISSIONS && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="roles-page">
      <SectionHeader
        title={`Roles`}
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        handleAdd={handleAddRole}
        handleDelete={() => setOpenConfirm(true)}
        disableDelete={!selection?.length}
        loading={loadingRoles || loadingDeleteRol}
        showButtons={hasWriteAccess}
      />
      {/* Filtros */}
      <RoleListFilter
        clearFilters={clearFilters}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        control={control}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        disableFilters={loadingRoles}
        filtersApplied={filtersApplied}
        loadingFilters={loadingRoles}
        disableClear={isFormEmpty}
      />
      {/* Tabla */}
      <Flex flexDir="column">
        {Boolean(!loadingRoles && !availableRoles?.length) && <NoData />}
        {Boolean(!loadingRoles && availableRoles?.length) && (
          <StandardTable
            indeterminate={indeterminate}
            items={
              availableRoles.map((rol) => ({
                ...rol,
                fechaCreacion: dayjs(rol.fechaCreacion).format(
                  "DD/MM/YYYY HH:mma",
                ),
                fechaModificacion: dayjs(rol.fechaModificacion).format(
                  "DD/MM/YYYY HH:mma",
                ),
                activo: rol?.activo ? "Activo" : "Inactivo",
              })) || []
            }
            selection={selection}
            setSelection={setSelection}
            headers={headerElements}
            noActions={!hasWriteAccess}
            noSelection={!hasWriteAccess}
            actionElements={getActionElements}
            actionsHeaderLabel="Edición"
            multipleSelection={false}
          />
        )}
      </Flex>
      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirm}
          handleAccept={handleDeleteRole}
          loadingAccept={loadingDeleteRol}
          title="Eliminar rol"
          subtitle="¿Está seguro de que desea realizar esta acción?"
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
